import React from "react";
import { House, Zap, Droplets, Wind, Info, ShieldCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const inspectionSystems = [{
  title: "Roofing System",
  description: "Evaluates the age, material type, and overall condition of the roof covering to identify any visible signs of damage, wear, or active leaks.",
  icon: House
}, {
  title: "Electrical System",
  description: "Checks the main electrical panel, wiring types, and overall system capacity to identify obvious fire hazards or significantly outdated components.",
  icon: Zap
}, {
  title: "Plumbing System",
  description: "Inspects visible pipes, water heaters, and fixtures to identify the materials used and check for active leaks or signs of previous water damage.",
  icon: Droplets
}, {
  title: "HVAC System",
  description: "Assesses the age and visible condition of the central heating and cooling equipment to determine general functionality and maintenance levels.",
  icon: Wind
}];
export default function WhatIsA4PointInspection() {
  return <section id="what-is-a-4-point-inspection" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {/* Section Header & Overview */}
        <div className="flex flex-col items-center text-center mb-16">
          <div className="max-w-4xl space-y-6 flex flex-col items-center">
            <Badge variant="secondary" className="bg-secondary/10 text-secondary-foreground hover:bg-secondary/20">
              <ShieldCheck className="w-4 h-4 mr-1" />
              Insurance Focused
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
              What Is a 4-Point Inspection?
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                A 4-point inspection is a specialized, targeted evaluation typically requested by insurance companies before issuing or renewing a homeowner's policy on an older home. 
              </p>
              <p>
                Unlike a comprehensive buyer's home inspection, this assessment focuses strictly on four specific critical systems. The goal is to determine the current condition, age, and overall maintenance level of these components based solely on what is visible and accessible at the time of the inspection.
              </p>
            </div>
            <div className="pt-4">
              <Button asChild size="lg">
                <Link to="https://trusted-fl.com/#schedule-your-inspection">Schedule Your Inspection</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* The 4 Systems */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold font-default text-center mb-10">The Four Critical Systems</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {inspectionSystems.map((system, index) => {
            const Icon = system.icon;
            return <Card key={index} data-index={index} className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 bg-card">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-default">{system.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {system.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Limitations & Disclaimers Callout */}
        <div className="max-w-4xl mx-auto">
          <Alert className="bg-accent/50 border-accent text-accent-foreground">
            <Info className="w-5 h-5 text-primary" />
            <AlertTitle className="text-lg font-semibold font-default mb-2">Important Limitations</AlertTitle>
            <AlertDescription className="text-base leading-relaxed opacity-90">
              This report is completed based on the visible and accessible components at the exact time of the inspection. It is designed to provide information to your insurance carrier regarding the current state of these four systems. Please note that this inspection report is a statement of condition, not a warranty, and it does not guarantee that an insurance company will issue a policy or provide coverage.
            </AlertDescription>
          </Alert>
        </div>

      </div>
    </section>;
}